import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

const SA_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape',
];

const personalInfoSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  idNumber: z
    .string()
    .length(13, 'SA ID number must be exactly 13 digits')
    .regex(/^\d{13}$/, 'ID number must contain only digits'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  streetAddress: z.string().min(5, 'Please enter a valid street address'),
  city: z.string().min(2, 'City is required'),
  province: z.string().min(1, 'Please select a province'),
  postalCode: z
    .string()
    .regex(/^\d{4}$/, 'Postal code must be 4 digits'),
});

type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;

interface PersonalInfoStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function PersonalInfoStep({ onNext, onBack }: PersonalInfoStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [idValidation, setIdValidation] = useState<{
    isValid: boolean;
    dateOfBirth?: string;
    gender?: string;
  } | null>(null);
  const { requestId, personalInfo, setPersonalInfo, setStep } = useVerificationStore();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: personalInfo || undefined,
  });

  const idNumber = watch('idNumber');

  // Validate ID number and auto-fill DOB
  useEffect(() => {
    const validateId = async () => {
      if (idNumber?.length === 13) {
        try {
          const response = await verificationApi.validateId(idNumber);
          setIdValidation(response.data);
          if (response.data.isValid && response.data.dateOfBirth) {
            setValue('dateOfBirth', response.data.dateOfBirth.split('T')[0]);
          }
        } catch {
          setIdValidation({ isValid: false });
        }
      } else {
        setIdValidation(null);
      }
    };
    validateId();
  }, [idNumber, setValue]);

  const onSubmit = async (data: PersonalInfoFormData) => {
    if (!requestId) return;

    if (!idValidation?.isValid) {
      setError('Please enter a valid South African ID number');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // Capture geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await verificationApi.updateGeolocation(requestId, {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            } catch {
              // Continue even if geolocation fails
            }
          },
          () => {
            // Continue without geolocation if permission denied
          }
        );
      }

      await verificationApi.updatePersonalInfo(requestId, data);
      setPersonalInfo(data);
      setStep('otp-verification');
      onNext();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save personal information');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
        <p className="text-sm text-gray-600 mt-1">
          Please provide your details exactly as they appear on your South African ID document.
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="label">
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              className={`input ${errors.firstName ? 'input-error' : ''}`}
              {...register('firstName')}
            />
            {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
          </div>

          <div>
            <label htmlFor="lastName" className="label">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              className={`input ${errors.lastName ? 'input-error' : ''}`}
              {...register('lastName')}
            />
            {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="idNumber" className="label">
              SA ID Number
            </label>
            <input
              id="idNumber"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={13}
              placeholder="e.g., 8501015009087"
              className={`input ${errors.idNumber || idValidation?.isValid === false ? 'input-error' : ''}`}
              {...register('idNumber')}
            />
            {errors.idNumber && <p className="error-text">{errors.idNumber.message}</p>}
            {idValidation?.isValid === false && !errors.idNumber && (
              <p className="error-text">Invalid SA ID number</p>
            )}
            {idValidation?.isValid && (
              <p className="text-sm text-green-600 mt-1">
                Valid ID - Gender: {idValidation.gender}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="dateOfBirth" className="label">
              Date of Birth
            </label>
            <input
              id="dateOfBirth"
              type="date"
              className={`input ${errors.dateOfBirth ? 'input-error' : ''}`}
              {...register('dateOfBirth')}
            />
            {errors.dateOfBirth && <p className="error-text">{errors.dateOfBirth.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="streetAddress" className="label">
            Street Address
          </label>
          <input
            id="streetAddress"
            type="text"
            placeholder="123 Main Street, Apartment 4B"
            className={`input ${errors.streetAddress ? 'input-error' : ''}`}
            {...register('streetAddress')}
          />
          {errors.streetAddress && <p className="error-text">{errors.streetAddress.message}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="city" className="label">
              City
            </label>
            <input
              id="city"
              type="text"
              className={`input ${errors.city ? 'input-error' : ''}`}
              {...register('city')}
            />
            {errors.city && <p className="error-text">{errors.city.message}</p>}
          </div>

          <div>
            <label htmlFor="province" className="label">
              Province
            </label>
            <select
              id="province"
              className={`input ${errors.province ? 'input-error' : ''}`}
              {...register('province')}
            >
              <option value="">Select province</option>
              {SA_PROVINCES.map((province) => (
                <option key={province} value={province}>
                  {province}
                </option>
              ))}
            </select>
            {errors.province && <p className="error-text">{errors.province.message}</p>}
          </div>

          <div>
            <label htmlFor="postalCode" className="label">
              Postal Code
            </label>
            <input
              id="postalCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder="0001"
              className={`input ${errors.postalCode ? 'input-error' : ''}`}
              {...register('postalCode')}
            />
            {errors.postalCode && <p className="error-text">{errors.postalCode.message}</p>}
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <button type="button" onClick={onBack} className="btn-secondary">
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" /> : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
